import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';

import { FinnairServiceItemWithUpsell } from '../interfaces';
import {
  FinnairBoundItem,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairTravelEndpoint,
} from '../api/models';

const getFlightOD = (flight: { arrival: FinnairTravelEndpoint; departure: FinnairTravelEndpoint }) =>
  `${flight.departure.locationCode}-${flight.arrival.locationCode}`;

const isInfant = (passenger: FinnairPassengerItem) => passenger.passengerTypeCode === FinnairPassengerCode.INF;

export const getSeatsByFlight = (
  passengers: FinnairPassengerItem[],
  currentBound: FinnairBoundItem,
  serviceItem: FinnairServiceItemWithUpsell
): Map<string, string[]> => {
  const flights = new Map<string, string[]>();
  currentBound.itinerary
    .filter(isFlight)
    .flatMap((flight) => {
      flights.set(getFlightOD(flight), []);
      return passengers.filter((passenger) => !isInfant(passenger)).map((passenger) => ({ flight, passenger }));
    })
    .forEach(({ flight, passenger }) => {
      const seatForPassengerInFlight = serviceItem.bounds
        .filter((bound) => bound.id === currentBound.id)
        .flatMap((bound) => bound.segments)
        .filter((serviceSegment) => serviceSegment.id === flight.id)
        .flatMap((serviceSegment) => serviceSegment.passengers)
        .filter((servicePax) => servicePax.id === passenger.id)
        .flatMap((servicePax) => servicePax.services)
        .flatMap((servicePaxService) => servicePaxService.seatNumber)
        .filter((seatNumber) => seatNumber !== null && seatNumber.length > 0);
      if (seatForPassengerInFlight?.length > 0) {
        const seatNumber = seatForPassengerInFlight[0];
        flights.get(getFlightOD(flight)).push(seatNumber);
      } else {
        flights.get(getFlightOD(flight)).push('--');
      }
    });
  return flights;
};

const EXCLUDE_FROM_SUMMARY_MEALS_VARIANTS = [
  'SH_ECO_MEAL_DEFAULT_1',
  'SH_ECO_MEAL_DEFAULT_2',
  'SH_ECO_DRINK_DEFAULT_1',
];

export const getMealSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell
): { quantity?: number; description: string } => {
  const allMeals = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .flatMap((flight) => flight.passengers)
    .flatMap((pax) => pax.services)
    .filter(({ variant }) => !EXCLUDE_FROM_SUMMARY_MEALS_VARIANTS.includes(variant));
  const paidCounts = allMeals
    .filter((paxService) => !paxService.includedInTicketType && !paxService.includedInTierBenefit)
    .flatMap((paxService) => paxService.quantity);
  const anyIncluded = allMeals.some(
    (paxService) => paxService.includedInTicketType || paxService.includedInTierBenefit
  );
  const totalQuantity = paidCounts.reduce((total, count) => {
    total += count;
    return total;
  }, 0);
  if (totalQuantity > 0) {
    return { quantity: totalQuantity, description: 'MMB.services.meals.paid' };
  } else if (anyIncluded) {
    return { description: 'MMB.services.meals.included' };
  }
  return { description: 'MMB.services.meals.noneIncluded' };
};

const getPaxServiceDisplayName = (paxService: FinnairPassengerServiceSelectionItem): string => {
  return paxService.parameters?.baggageWeight > 0
    ? paxService.displayName.replace('{weight}', '' + paxService.parameters.baggageWeight)
    : paxService.displayName;
};

export const getCheckedBaggageSummary = (
  currentBound: FinnairBoundItem,
  service: FinnairServiceItemWithUpsell
): { quantity?: number; description: string }[] => {
  const countsByFlightByVariant = service.bounds
    .filter((bound) => bound.id === currentBound.id)
    .flatMap((bound) => bound.segments)
    .map((flight) => {
      //for each flight:
      const countsInFlight = flight.passengers
        .flatMap((pax) => pax.services)
        .flatMap((paxService) => ({
          description: getPaxServiceDisplayName(paxService),
          quantity: paxService.quantity,
        }));
      return countsInFlight.reduce((countsInFlightByVariant, countItem) => {
        let countForVariant = countsInFlightByVariant.get(countItem.description) || 0;
        countForVariant += countItem.quantity;
        countsInFlightByVariant.set(countItem.description, countForVariant);
        return countsInFlightByVariant;
      }, new Map<string, number>());
    });
  //max count per flight and subCategory
  const maxCountByVariant = countsByFlightByVariant.reduce((perVariant, flightCount) => {
    //find max
    flightCount.forEach((count, variant) => {
      const maxCount = perVariant.get(variant) || 0;
      perVariant.set(variant, Math.max(maxCount, count));
    });
    return perVariant;
  }, new Map<string, number>());
  const summaryItems = [];
  maxCountByVariant.forEach((countInBound, variant) => {
    summaryItems.push({ description: variant, quantity: countInBound });
  });
  return summaryItems;
};
